import type { CreateApprovalRequestInput as GeneratedCreateApprovalRequestInput } from '@/libs/api-user-mgt/generated/model';
import { axiosInstance } from '@/libs/api-user-mgt/axios-instance';
import {
  getApiUserManagementV1ApprovalRequestsRequestId,
  getApiUserManagementV1ApprovalRequestsSummary,
  postApiUserManagementV1ApprovalRequests,
  postApiUserManagementV1ApprovalRequestsRequestIdApply,
  postApiUserManagementV1ApprovalRequestsRequestIdApprove,
  postApiUserManagementV1ApprovalRequestsRequestIdReject
} from '@/libs/api-user-mgt/generated/approvals/approvals';

import { mapApprovalDtoToEntity, type ApprovalWireDto } from '@/userManagement/application/mappers/approvalMapper';
import type { ApprovalRequest, ApprovalSummary } from '@/userManagement/domain/entities/ApprovalRequest';
import type { PaginatedResponse, SieveParams } from '@/userManagement/domain/types/QueryContracts';
import type {
  ApproveRequestInput,
  CreateApprovalRequestInput,
  RejectRequestInput
} from '@/userManagement/application/types/UserManagementApiContracts';

import { UserManagementGapError, USER_MANAGEMENT_GAP_MESSAGE } from '@/userManagement/domain/errors/UserManagementGapError';
import { requireMinLengthReviewNotes, resolveApplyJustification } from '@/userManagement/application/utils/reviewNotesValidation';

import { fetchUserManagementPaged } from './pagination';

const MAX_PENDING_APPROVAL_PAGES = 25;

function isPermissionChangeApprovalRow(dto: ApprovalWireDto): boolean {
  const o = dto as unknown as Record<string, unknown>;
  const ct = o.changeType ?? o.ChangeType;
  return ct === 'PermissionChange' || String(ct ?? '').trim() === 'PermissionChange';
}

/**
 * Counts in-flight permission approval requests per target username (lowercase).
 * Used when snapshot comparison rows omit `inProgressApprovalChangeCount` from the server.
 */
export async function fetchPendingPermissionApprovalCountByUsernameLower(): Promise<Map<string, number>> {
  const counts = new Map<string, number>();
  let page = 1;
  const pageSize = 100;
  for (let i = 0; i < MAX_PENDING_APPROVAL_PAGES; i += 1) {
    const r = await fetchUserManagementPaged<ApprovalWireDto>('approvalRequests', {
      page,
      pageSize,
      status: 'PendingApproval'
    } as unknown as Record<string, unknown>);
    for (const raw of r.data) {
      const dto = raw as ApprovalWireDto;
      if (!isPermissionChangeApprovalRow(dto)) continue;
      const uname = (
        dto.targetUsername ??
        dto.targetUserName ??
        (dto as unknown as { TargetUsername?: string }).TargetUsername ??
        (dto as unknown as { TargetUserName?: string }).TargetUserName ??
        ''
      )
        .trim()
        .toLowerCase();
      if (uname === '') continue;
      counts.set(uname, (counts.get(uname) ?? 0) + 1);
    }
    if (r.data.length < pageSize || page >= r.totalPages) break;
    page += 1;
  }
  return counts;
}

/** OpenAPI client uses `void` response generics; UM returns approval DTO bodies at runtime. */
function approvalResponseData(data: unknown): ApprovalWireDto {
  return data as ApprovalWireDto;
}

export const userManagementApprovalsSegment = {
  listApprovalRequests: (params: SieveParams): Promise<PaginatedResponse<ApprovalWireDto>> =>
    fetchUserManagementPaged<ApprovalWireDto>('approvalRequests', params as Record<string, unknown>),

  getApprovalRequest: async (requestId: number): Promise<ApprovalWireDto> => {
    const r = await getApiUserManagementV1ApprovalRequestsRequestId(requestId);
    return approvalResponseData(r.data);
  },

  createApprovalRequest: async (data: CreateApprovalRequestInput, idempotencyKey: string): Promise<ApprovalRequest> => {
    const delta = data.permissionChangeDelta;
    const hasDelta = Boolean(delta?.changes && delta.changes.length > 0);
    const legacyRows = data.permissions ?? [];
    const hasLegacyPermissions = legacyRows.length > 0;
    if (hasDelta && hasLegacyPermissions) {
      throw new Error('Invalid approval request: send permissionChangeDelta or permissions, not both.');
    }

    const body = {
      targetUsername: data.targetUsername,
      changeType: data.changeType,
      dbName: data.dbName,
      justification: data.justification,
      changeCategory: data.changeCategory,
      databases: data.databases ?? null,
      newRoleId: data.newRoleId ?? null,
      ...(hasDelta
        ? {
            permissionChangeDelta: delta
          }
        : hasLegacyPermissions
          ? {
              permissions: legacyRows.map((p) => ({
                featureId: p.featureId,
                allowRead: p.allowRead,
                allowWrite: p.allowWrite,
                allowDelete: p.allowDelete,
                allowCentralAccess: p.allowCentralAccess,
                allowExcelExport: p.allowExcelExport
              }))
            }
          : {})
    } as GeneratedCreateApprovalRequestInput;
    const r = await postApiUserManagementV1ApprovalRequests(body, {
      headers: { 'X-Idempotency-Key': idempotencyKey }
    });
    return mapApprovalDtoToEntity(approvalResponseData(r.data));
  },

  approveRequest: async (requestId: number, data: ApproveRequestInput): Promise<ApprovalRequest> => {
    await postApiUserManagementV1ApprovalRequestsRequestIdApprove(requestId, {
      reviewNotes: requireMinLengthReviewNotes(data.reviewNotes, 'Review notes')
    });
    const r = await getApiUserManagementV1ApprovalRequestsRequestId(requestId);
    return mapApprovalDtoToEntity(approvalResponseData(r.data));
  },

  rejectRequest: async (requestId: number, data: RejectRequestInput): Promise<ApprovalRequest> => {
    await postApiUserManagementV1ApprovalRequestsRequestIdReject(requestId, {
      reviewNotes: requireMinLengthReviewNotes(data.reason, 'Rejection reason')
    });
    const r = await getApiUserManagementV1ApprovalRequestsRequestId(requestId);
    return mapApprovalDtoToEntity(approvalResponseData(r.data));
  },

  applyRequest: async (requestId: number, data?: ApproveRequestInput): Promise<void> => {
    const justification = resolveApplyJustification(data?.reviewNotes, 'Applied via User Management UI.');
    await postApiUserManagementV1ApprovalRequestsRequestIdApply(requestId, { justification });
  },

  /** TROC-155: 204 No Content — refresh list/detail/summary via query invalidation. */
  cancelRequest: async (requestId: number, cancellationReason: string): Promise<void> => {
    await axiosInstance<void>({
      url: `/api/user-management/v1/approval-requests/${requestId}/cancel`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: { cancellationReason }
    });
  },

  getApprovalSummary: async (): Promise<ApprovalSummary> => {
    const r = await getApiUserManagementV1ApprovalRequestsSummary();
    const raw = r.data;
    if (raw === null || raw === undefined || typeof raw !== 'object') {
      throw new UserManagementGapError(USER_MANAGEMENT_GAP_MESSAGE.approvalSummaryShape, 'approvalSummaryShape');
    }
    const o = raw as Record<string, unknown>;
    const n = (camel: string, pascal: string) => {
      const v = o[camel] ?? o[pascal];
      if (typeof v === 'number' && Number.isFinite(v)) return v;
      if (typeof v === 'string' && v.trim() !== '') {
        const parsed = Number(v);
        if (Number.isFinite(parsed)) return parsed;
      }
      return 0;
    };
    if ('pendingReview' in o || 'PendingReview' in o || 'pendingIT' in o || 'PendingIT' in o) {
      return {
        pendingReview: n('pendingReview', 'PendingReview'),
        pendingIT: n('pendingIT', 'PendingIT'),
        approved: n('approved', 'Approved'),
        rejected: n('rejected', 'Rejected'),
        cancelled: n('cancelled', 'Cancelled'),
        total: n('total', 'Total')
      };
    }
    throw new UserManagementGapError(USER_MANAGEMENT_GAP_MESSAGE.approvalSummaryShape, 'approvalSummaryShape');
  }
};
